
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Redirect, RedirectStats } from "@/types/redirectTypes";
import { 
  getRedirects, 
  getRedirectStats, 
  deleteRedirect, 
  bulkImportRedirects, 
  exportRedirects 
} from "@/services/redirectService";

export const useRedirectsManager = () => {
  const { toast } = useToast();
  const [redirects, setRedirects] = useState<Redirect[]>([]);
  const [filteredRedirects, setFilteredRedirects] = useState<Redirect[]>([]);
  const [stats, setStats] = useState<RedirectStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [redirectToEdit, setRedirectToEdit] = useState<Redirect | null>(null);
  const [showRedirectForm, setShowRedirectForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [redirectToDelete, setRedirectToDelete] = useState<Redirect | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Load redirects
  const loadRedirects = async () => {
    setIsLoading(true);
    try {
      const data = await getRedirects();
      setRedirects(data);
      
      // Also fetch stats
      const statsData = await getRedirectStats();
      setStats(statsData);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to load redirects",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter redirects based on search term and status
  useEffect(() => {
    let filtered = [...redirects];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        r => r.source_path.toLowerCase().includes(term) || 
             r.target_path.toLowerCase().includes(term) ||
             (r.notes && r.notes.toLowerCase().includes(term))
      );
    }
    
    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(r => 
        statusFilter === "active" ? r.is_active : !r.is_active
      );
    }
    
    setFilteredRedirects(filtered);
  }, [redirects, searchTerm, statusFilter]);

  // Load redirects on component mount
  useEffect(() => {
    loadRedirects();
  }, []);

  // Create new redirect
  const handleCreateRedirect = () => {
    setRedirectToEdit(null);
    setShowRedirectForm(true);
  };

  // Edit existing redirect
  const handleEditRedirect = (redirect: Redirect) => {
    setRedirectToEdit(redirect);
    setShowRedirectForm(true);
  };

  // Delete redirect
  const handleDeleteClick = (redirect: Redirect) => {
    setRedirectToDelete(redirect);
    setShowDeleteDialog(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!redirectToDelete) return;
    
    try {
      await deleteRedirect(redirectToDelete.id);
      setRedirects(redirects.filter(r => r.id !== redirectToDelete.id));
      toast({
        title: "Success",
        description: "Redirect deleted successfully",
      });
      setShowDeleteDialog(false);
      setRedirectToDelete(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete redirect",
        variant: "destructive",
      });
    }
  };

  // Bulk import
  const handleBulkImport = async (csv: string) => {
    setIsImporting(true);
    
    try {
      // Parse CSV
      const rows = csv.split("\n").filter(row => row.trim());
      const headers = rows[0].split(",").map(h => h.trim().toLowerCase());
      
      const sourcePathIndex = headers.indexOf("source_path");
      const targetPathIndex = headers.indexOf("target_path");
      const statusCodeIndex = headers.indexOf("status_code");
      const isActiveIndex = headers.indexOf("is_active");
      const notesIndex = headers.indexOf("notes");
      
      if (sourcePathIndex === -1 || targetPathIndex === -1) {
        throw new Error("CSV must contain source_path and target_path columns");
      }
      
      // Parse data
      const redirects = rows.slice(1).map(row => {
        const columns = row.split(",").map(c => c.trim());
        
        // Ensure paths start with /
        let sourcePath = columns[sourcePathIndex];
        if (!sourcePath.startsWith("/")) sourcePath = "/" + sourcePath;
        
        let targetPath = columns[targetPathIndex];
        if (!targetPath.startsWith("/")) targetPath = "/" + targetPath;
        
        return {
          source_path: sourcePath,
          target_path: targetPath,
          status_code: statusCodeIndex !== -1 
            ? (parseInt(columns[statusCodeIndex]) as 301 | 302 | 307 | 308) || 301 
            : 301,
          is_active: isActiveIndex !== -1 
            ? columns[isActiveIndex].toLowerCase() === "true" 
            : true,
          notes: notesIndex !== -1 ? columns[notesIndex] : undefined
        };
      });
      
      // Import redirects
      const result = await bulkImportRedirects(redirects);
      
      toast({
        title: "Import complete",
        description: `Successfully imported ${result.success} redirects. ${result.failed} failed.`,
        variant: result.failed > 0 ? "destructive" : "default",
      });
      
      // Reload redirects
      await loadRedirects();
      setShowImportDialog(false);
    } catch (error: any) {
      toast({
        title: "Import failed",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  // Export redirects
  const handleExport = async () => {
    try {
      const data = await exportRedirects();
      
      // Convert to CSV
      const headers = "source_path,target_path,status_code,is_active,notes,access_count,last_accessed_at";
      const csvRows = data.map(r => 
        `${r.source_path},${r.target_path},${r.status_code},${r.is_active},${r.notes || ""},${r.access_count},${r.last_accessed_at || ""}`
      );
      const csv = [headers, ...csvRows].join("\n");
      
      // Download file
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `redirects-export-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Export complete",
        description: `${data.length} redirects exported to CSV`,
      });
    } catch (error: any) {
      toast({
        title: "Export failed",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  // Handle successful redirect save
  const handleRedirectSaved = (redirect: Redirect) => {
    if (redirectToEdit) {
      // Update existing redirect in the list
      setRedirects(redirects.map(r => r.id === redirect.id ? redirect : r));
    } else {
      // Add new redirect to the list
      setRedirects([...redirects, redirect]);
    }
    
    setShowRedirectForm(false);
    setRedirectToEdit(null);
  };

  return {
    redirects: filteredRedirects,
    stats,
    isLoading,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    redirectToEdit,
    showRedirectForm,
    setShowRedirectForm,
    showDeleteDialog,
    setShowDeleteDialog,
    redirectToDelete,
    showImportDialog,
    setShowImportDialog,
    isImporting,
    handleCreateRedirect,
    handleEditRedirect,
    handleDeleteClick,
    handleConfirmDelete,
    handleBulkImport,
    handleExport,
    handleRedirectSaved,
    refreshRedirects: loadRedirects
  };
};
